/**
 * 驳回轨道对话框
 */

import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import { TRACK_DISPLAY_NAMES } from '../workflow-constants'

import type { TrackType } from '@company/shared'

// ==================== 类型定义 ====================

interface RejectTrackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  track: TrackType
  onConfirm: (reason: string) => void
  loading?: boolean
  error?: string | null
}

// ==================== 组件实现 ====================

export function RejectTrackDialog({
  open,
  onOpenChange,
  track,
  onConfirm,
  loading = false,
  error,
}: RejectTrackDialogProps) {
  const [reason, setReason] = useState('')

  const trackName = TRACK_DISPLAY_NAMES[track]

  const handleConfirm = () => {
    if (!reason.trim()) {
      return
    }
    onConfirm(reason)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setReason('')
    }
    onOpenChange(open)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>驳回{trackName}代码</AlertDialogTitle>
          <AlertDialogDescription>请输入驳回原因</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="请输入驳回原因"
            rows={3}
            className="bg-background w-full rounded-md border px-3 py-2 text-sm"
          />

          {/* 错误提示 */}
          {error && (
            <div className="bg-destructive/10 text-destructive mt-4 rounded-md p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading || !reason.trim()}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? '处理中...' : '确认驳回'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * 确认审批对话框
 */
interface ApproveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  onConfirm: () => void
  loading?: boolean
  error?: string | null
}

export function ApproveDialog({
  open,
  onOpenChange,
  title = '确认审批通过',
  description = '确定要审批通过吗？任务将流转到下一阶段。',
  onConfirm,
  loading = false,
  error,
}: ApproveDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">{error}</div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? '审批中...' : '确认'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
